import {
  BookOpenIcon,
  CreditCardIcon,
  PencilIcon
} from '@heroicons/react/24/outline';
import { KebabMenu, KebabMenuItem } from '@library/components/menus';
import { useCallback, useState } from 'react';
import { AddIconButton } from '@library/components/buttons/AddIconButton';
import { Button } from '@library/components/buttons';
import { MEMBER_GROUP_PERMISSIONS } from '@bo-constants/permissions';
import { MemberGroupsCSVUpload } from '@bo-components/memberGroups/MemberGroupsCSVUpload';
import { MemberGroupsContext } from '@bo-hooks/memberGroups/useMemberGroupsContext';
import { MemberGroupsFilters } from '@bo-components/memberGroups/MemberGroupsFilters';
import { MemberGroupsMemberTable } from '@bo-components/memberGroups/MemberGroupsMemberTable';
import { MemberGroupsModals } from '@bo-components/memberGroups/MemberGroupsModals';
import { MemberGroupsTable } from '@bo-components/memberGroups/MemberGroupsTable';
import { PATHS } from '@bo-constants/routes';
import { PageTitle } from '@library/components/PageTitle';
import { PaymentModifierDrawer } from '@bo-components/memberGroups/PaymentModifierDrawer';
import { useHasRolePermission } from '@bo-hooks/permissions/useHasRolePermission';
import { useMemberGroups } from '@bo-hooks/memberGroups/useMemberGroups';
import { useNavigate } from 'react-router-dom';
import { useSentrySetDashBoardTag } from '@bo-hooks/sentry/useSentrySetDashBoardTag';

const {
  MEMBER_GROUPS_CREATE,
  MEMBER_GROUPS_CSV_UPLOAD,
  MEMBER_GROUPS_VIEW_RULESET
} = MEMBER_GROUP_PERMISSIONS;

const kebabMenuItemClassName =
  'text-soClean-100 hover:bg-soFresh-120 flex h-7 w-full items-center gap-2 rounded-md p-6';

export const MemberGroupsDashboard = () => {
  useSentrySetDashBoardTag({ dashboardName: 'MemberGroups' });

  const memberGroupsContext = useMemberGroups();
  const { openModal, currentMemberGroup } = memberGroupsContext;
  const navigate = useNavigate();
  const canCreateMemberGroup = useHasRolePermission(MEMBER_GROUPS_CREATE);
  const canUploadCSV = useHasRolePermission(MEMBER_GROUPS_CSV_UPLOAD);
  const canViewRuleset = useHasRolePermission(MEMBER_GROUPS_VIEW_RULESET);

  const [paymentModifierDrawerOpen, setPaymentModifierDrawerOpen] =
    useState<boolean>(false);

  const handleCreateMemberGroup = useCallback(
    () => openModal('create'),
    [openModal]
  );

  const handleOpenRulesets = useCallback(() => {
    if (currentMemberGroup) {
      navigate(`${PATHS.RULESETS}?memberGroupId=${currentMemberGroup.id}`);
    }
  }, [currentMemberGroup, navigate]);

  const handleOpenPaymentModifier = useCallback(() => {
    setPaymentModifierDrawerOpen(true);
  }, []);

  const handleClosePaymentModifier = useCallback(() => {
    setPaymentModifierDrawerOpen(false);
  }, []);

  const handleEditMemberGroup = useCallback(() => {
    openModal('edit', currentMemberGroup?.id);
  }, [currentMemberGroup, openModal]);

  return (
    <div className="flex w-auto flex-col gap-4 px-6 pb-2 pt-3">
      <div className="border-soFresh-140 -mx-6 flex items-end gap-4 border-b px-6 pb-4">
        <div>
          <PageTitle
            title="Member Groups"
            className="text-soClean-180 text-sm !font-normal"
          />
          <span className="text-soClean-100 text-xl font-semibold">
            {currentMemberGroup ? String(currentMemberGroup.name) : 'All'}
          </span>
        </div>

        <div className="grow" />

        {!currentMemberGroup && canUploadCSV && <MemberGroupsCSVUpload />}

        {currentMemberGroup && canViewRuleset && (
          <Button
            title="View Ruleset"
            onClick={handleOpenRulesets}
            className="text-soClean-120 mb-2"
          >
            <BookOpenIcon className="h-[1.25em]" />
          </Button>
        )}

        {!currentMemberGroup && canCreateMemberGroup && (
          <AddIconButton
            className="bg-stankonia-200 hover:bg-stankonia-100 text-soClean-100 !w-auto px-4 py-2"
            label="New Group"
            onClick={handleCreateMemberGroup}
          />
        )}

        {currentMemberGroup && (
          <KebabMenu
            title="MemberGroupKebabMenu"
            menuItemsClassName="border border-soFresh-140 bg-soFresh-110 z-10 right-0 text-xs rounded-md"
            menuButtonOpenClassName="rounded-md bg-soFresh-140"
          >
            {canViewRuleset && (
              <KebabMenuItem
                className={kebabMenuItemClassName}
                handleOnClick={handleOpenRulesets}
              >
                <BookOpenIcon className="h-[1.25em]" />
                View Ruleset
              </KebabMenuItem>
            )}

            <KebabMenuItem
              className={kebabMenuItemClassName}
              handleOnClick={handleEditMemberGroup}
            >
              <PencilIcon className="h-[1.25em]" />
              Edit Member Group
            </KebabMenuItem>

            <KebabMenuItem
              className={kebabMenuItemClassName}
              handleOnClick={handleOpenPaymentModifier}
            >
              <CreditCardIcon className="h-[1.25em]" />
              Configure Payment
            </KebabMenuItem>
          </KebabMenu>
        )}
      </div>

      <MemberGroupsContext.Provider value={memberGroupsContext}>
        {currentMemberGroup ? (
          <>
            <MemberGroupsMemberTable />
            <PaymentModifierDrawer
              memberGroup={currentMemberGroup}
              open={paymentModifierDrawerOpen}
              onClose={handleClosePaymentModifier}
            />
          </>
        ) : (
          <>
            <MemberGroupsFilters />
            <MemberGroupsTable />
          </>
        )}
        <MemberGroupsModals />
      </MemberGroupsContext.Provider>
    </div>
  );
};
